// ImportModal.tsx
import * as React from 'react'
import { useCallback, useMemo, useState } from 'react'
import Dropzone from 'react-dropzone'
import type { DropzoneOptions } from 'react-dropzone'

import { Loader } from '@/ui'
import { useImportedFiles } from '../hooks/useImportedFiles'
import { IMPORT_DEFAULT_ACCEPT } from '../config'
import { useLocalize } from '@/modules/shared/context/localize/LocalizeContext'

type FileRejection =
  NonNullable<DropzoneOptions['onDropRejected']> extends (arg: infer U) => any
    ? U
    : never

type ImportModalProps = {
//   open: boolean
//   onClose: () => void
  title?: string
  handleUpload: (files: File[]) => Promise<void> | void
  isUploading: boolean
}

function formatBytes(bytes: number): string {
  if (!Number.isFinite(bytes) || bytes <= 0) return '0 B'
  const units = ['B', 'KB', 'MB', 'GB', 'TB']
  const i = Math.min(
    Math.floor(Math.log(bytes) / Math.log(1024)),
    units.length - 1,
  )
  const value = bytes / Math.pow(1024, i)
  return `${value.toFixed(value >= 10 || i === 0 ? 0 : 1)} ${units[i]}`
}

function fileKey(file: File) {
  return `${file.name}-${file.size}-${file.lastModified}`
}

export const PopupStepUploading = ({
//   onClose,
  title: titleProp,
  handleUpload,
  isUploading,
}: ImportModalProps) => {
  const { t } = useLocalize()
  const title = titleProp ?? t('importModule.loading-data-from-file')
  const { files, addFiles, clearFiles, removeFile } = useImportedFiles()
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [rejectionMsg, setRejectionMsg] = useState<string | null>(null)

  const busy = isUploading || isSubmitting

  const texts = useMemo(
    () => ({
      hintTitle: t('importModule.import-files'),
      hintSubtitle: t('importModule.drag-or-pick'),
      pick: t('importModule.pick-from-disk'),
      addMore: t('importModule.add-more'),
      upload: t('common.send'),
      clear: t('importModule.clear'),
      close: t('importModule.close-window'),
      remove: t('importModule.remove-file'),
      uploading: t('importModule.uploading'),
    }),
    [t],
  )

  const onDropAccepted = useCallback(
    (acceptedFiles: File[]) => {
      if (!acceptedFiles.length) return
      setRejectionMsg(null)
      addFiles(acceptedFiles)
    },
    [addFiles],
  )

  // Klucze MIME w accept dopasowują się same, więc bez tego .exe typu
  // octet-stream przeszedłby tak samo jak .ddd.
  const validateExtension = useCallback(
    (file: File) =>
      Object.values(IMPORT_DEFAULT_ACCEPT)
        .flat()
        .some((ext) => file.name.toLowerCase().endsWith(ext))
        ? null
        : { code: 'file-invalid-type', message: file.name },
    [],
  )

  const onDropRejected = useCallback((_rejected: FileRejection[]) => {
    setRejectionMsg(
      t('importModule.drop-rejected-msg'),
    )
  }, [t])

  const uploadAll = useCallback(async () => {
    if (!files.length || busy) return
    try {
      setIsSubmitting(true)
      await handleUpload(files)
      clearFiles()
      // onClose()
    } finally {
      setIsSubmitting(false)
    }
  }, [files, busy, handleUpload, clearFiles])

  const close = useCallback(() => {
    if (busy) return
    setRejectionMsg(null)
    // onClose()
  }, [busy])

  const canUpload = files.length > 0 && !busy

  return (
    // <Popup open={open} onClose={close}>
      <div className="tw-w-full tw-min-w-[320px]">
        {/* Header */}
        <div className="tw-mb-4 tw-flex tw-items-start tw-justify-between tw-gap-4">
          <div className="tw-min-w-0">
            <h2 className="tw-text-base tw-font-semibold tw-text-slate-900">
              {title}
            </h2>
            {!busy && (
              <p className="tw-mt-1 tw-text-sm tw-text-slate-500">
                {t('importModule.add-files-hint')}
              </p>
            )}
          </div>

          {/* <button
            type="button"
            onClick={close}
            aria-label={texts.close}
            disabled={busy}
            className={[
              'tw-shrink-0 tw-rounded-md tw-px-2 tw-py-1 tw-text-xl tw-leading-none',
              'tw-text-slate-400 hover:tw-bg-slate-50 hover:tw-text-slate-600',
              busy ? 'tw-cursor-not-allowed tw-opacity-50' : '',
            ].join(' ')}
          >
            ×
          </button> */}
        </div>

        {/* Body */}
        {busy ? (
          <div className="tw-flex tw-flex-col tw-items-center tw-justify-center tw-gap-3 tw-py-10">
            <Loader />
            <div className="tw-text-sm tw-text-slate-600">
              {texts.uploading}
            </div>
          </div>
        ) : (
          <Dropzone
            multiple
            onDropAccepted={onDropAccepted}
            onDropRejected={onDropRejected}
            accept={IMPORT_DEFAULT_ACCEPT}
            validator={validateExtension}
          >
            {({
              getRootProps,
              getInputProps,
              isDragActive,
              open: openDialog,
            }) => (
              <div className="tw-space-y-3">
                {/* Drop area */}
                <div
                  {...getRootProps({
                    className: [
                      'tw-rounded-xl tw-border tw-bg-white tw-px-6 tw-py-8',
                      'tw-flex tw-flex-col tw-items-center tw-justify-center tw-text-center',
                      'tw-transition tw-duration-150 tw-ease-out',
                      isDragActive
                        ? 'tw-border-blue-500 tw-bg-blue-50 tw-shadow-[0_0_0_2px_rgba(59,130,246,0.35)]'
                        : 'tw-border-slate-200 hover:tw-border-slate-300',
                    ].join(' '),
                  })}
                >
                  <input {...getInputProps()} />

                  <div className="tw-mb-3 tw-text-3xl" aria-hidden="true">
                    📄
                  </div>

                  <p className="tw-mb-1 tw-font-semibold tw-text-slate-900">
                    {texts.hintTitle}
                  </p>
                  <p className="tw-mb-4 tw-text-sm tw-text-slate-500">
                    {texts.hintSubtitle}
                  </p>

                  <div className="tw-flex tw-flex-wrap tw-justify-center tw-gap-2">
                    <button
                      type="button"
                      className="tw-inline-flex tw-items-center tw-justify-center tw-rounded-lg tw-bg-blue-600 tw-px-4 tw-py-2 tw-text-sm tw-font-medium tw-text-white tw-transition hover:tw-bg-blue-700"
                      onClick={(e) => {
                        e.preventDefault()
                        e.stopPropagation()
                        openDialog()
                      }}
                    >
                      {files.length ? texts.addMore : texts.pick}
                    </button>

                    {files.length ? (
                      <button
                        type="button"
                        className="tw-inline-flex tw-items-center tw-justify-center tw-rounded-lg tw-border tw-border-slate-200 tw-bg-white tw-px-4 tw-py-2 tw-text-sm tw-font-medium tw-text-slate-700 tw-transition hover:tw-bg-slate-50"
                        onClick={(e) => {
                          e.preventDefault()
                          e.stopPropagation()
                          clearFiles()
                        }}
                      >
                        {texts.clear}
                      </button>
                    ) : null}
                  </div>

                  {rejectionMsg ? (
                    <div className="tw-mt-3 tw-text-sm tw-text-red-600">
                      {rejectionMsg}
                    </div>
                  ) : null}
                </div>

                {/* Files list + primary action */}
                {files.length ? (
                  <div className="tw-rounded-xl tw-border tw-border-slate-200 tw-bg-white">
                    <div className="tw-flex tw-items-center tw-justify-between tw-border-b tw-border-slate-100 tw-px-4 tw-py-3">
                      <div className="tw-text-sm tw-font-medium tw-text-slate-900">
                        {t('importModule.selected-files')}{' '}
                        <span className="tw-text-slate-500">
                          ({files.length})
                        </span>
                      </div>

                      <button
                        type="button"
                        onClick={(e) => {
                          e.preventDefault()
                          openDialog()
                        }}
                        className="tw-text-sm tw-font-medium tw-text-blue-700 hover:tw-text-blue-800"
                      >
                        {texts.addMore}
                      </button>
                    </div>

                    <div className="tw-max-h-72 tw-overflow-auto tw-p-2">
                      <div className="tw-space-y-2">
                        {files.map((file, idx) => (
                          <div
                            key={`${fileKey(file)}-${idx}`}
                            className="tw-flex tw-items-center tw-justify-between tw-gap-3 tw-rounded-lg tw-border tw-border-slate-200 tw-bg-slate-50 tw-px-3 tw-py-2"
                          >
                            <div className="tw-min-w-0">
                              <div className="tw-truncate tw-text-sm tw-text-slate-800">
                                {file.name}
                              </div>
                              <div className="tw-text-xs tw-text-slate-500">
                                {formatBytes(file.size)}
                              </div>
                            </div>

                            <button
                              type="button"
                              onClick={() => removeFile(idx)}
                              aria-label={texts.remove}
                              className="tw-flex tw-h-8 tw-w-8 tw-items-center tw-justify-center tw-rounded-full tw-text-slate-400 tw-transition hover:tw-bg-red-100 hover:tw-text-red-600"
                            >
                              ✕
                            </button>
                          </div>
                        ))}
                      </div>
                    </div>

                    <div className="tw-flex tw-gap-2 tw-border-t tw-border-slate-100 tw-px-4 tw-py-3">
                      <button
                        type="button"
                        onClick={() => clearFiles()}
                        className="tw-flex-1 tw-w-1/3 tw-rounded-lg tw-border tw-border-slate-200 tw-bg-white tw-py-2 tw-text-sm tw-font-medium tw-text-slate-700 tw-transition hover:tw-bg-slate-50"
                      >
                        {texts.clear}
                      </button>

                      <button
                        type="button"
                        onClick={uploadAll}
                        disabled={!canUpload}
                        className={[
                          'tw-flex-1 tw-w-2/3 tw-rounded-lg tw-py-2 tw-text-sm tw-font-medium tw-text-white tw-transition',
                          canUpload
                            ? 'tw-bg-blue-600 hover:tw-bg-blue-700'
                            : 'tw-cursor-not-allowed tw-bg-slate-300',
                        ].join(' ')}
                      >
                        {texts.upload} ({files.length})
                      </button>
                    </div>
                  </div>
                ) : null}
              </div>
            )}
          </Dropzone>
        )}
      </div>
    // </Popup>
  )
}
